
// ... existing code ...

interface ServicesSectionProps {
  onOpenAuditModal: () => void;
}

export default function ServicesSection({ onOpenAuditModal }: ServicesSectionProps) {
  return (
    <section id="services" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Nos services d'externalisation comptable
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Une gamme complète de services comptables adaptés aux besoins des cabinets français, 
            avec la qualité et l'expertise que vous attendez.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
            <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-lg mb-6">
              <i className="ri-calculator-line text-2xl text-blue-600"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Tenue de comptabilité</h3>
            <p className="text-gray-600 mb-6">
              Saisie comptable, lettrage, rapprochements bancaires avec une précision irréprochable 
              et dans le respect des normes françaises.
            </p>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Saisie des écritures comptables</li>
              <li>• Lettrage automatique et manuel</li>
              <li>• Rapprochements bancaires</li>
              <li>• Gestion des immobilisations</li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-lg mb-6">
              <i className="ri-file-chart-line text-2xl text-green-600"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Établissement des comptes</h3>
            <p className="text-gray-600 mb-6">
              Comptes annuels, liasses fiscales et documents comptables conformes 
              à la réglementation française.
            </p>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Bilan et compte de résultat</li>
              <li>• Annexes comptables</li>
              <li>• Liasses fiscales 2033/2065</li>
              <li>• Révision comptable</li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
            <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-lg mb-6">
              <i className="ri-government-line text-2xl text-purple-600"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Déclarations fiscales</h3>
            <p className="text-gray-600 mb-6">
              TVA, IS, CFE, CVAE... Toutes vos déclarations fiscales traitées 
              avec expertise et dans les délais.
            </p>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Déclarations de TVA (CA3, CA12)</li>
              <li>• Impôt sur les sociétés</li>
              <li>• CFE et CVAE</li>
              <li>• DES et autres déclarations</li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
            <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-lg mb-6">
              <i className="ri-team-line text-2xl text-orange-600"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Paie et social</h3>
            <p className="text-gray-600 mb-6">
              Gestion complète de la paie française : bulletins, charges sociales, 
              déclarations et formalités administratives.
            </p>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Établissement des bulletins de paie</li>
              <li>• Déclarations sociales (DSN)</li>
              <li>• Gestion des congés payés</li>
              <li>• Formalités d'embauche/départ</li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
            <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-lg mb-6">
              <i className="ri-bar-chart-box-line text-2xl text-red-600"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Reporting et analyse</h3>
            <p className="text-gray-600 mb-6">
              Tableaux de bord personnalisés, analyses financières et reporting 
              pour accompagner vos clients dans leurs décisions.
            </p>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Tableaux de bord mensuels</li>
              <li>• Analyses de rentabilité</li>
              <li>• Budgets prévisionnels</li>
              <li>• Reporting personnalisé</li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
            <div className="w-16 h-16 flex items-center justify-center bg-indigo-100 rounded-lg mb-6">
              <i className="ri-customer-service-2-line text-2xl text-indigo-600"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Support dédié</h3>
            <p className="text-gray-600 mb-6">
              Un interlocuteur unique, expert-comptable inscrit à l'ordre, 
              disponible pour répondre à toutes vos questions.
            </p>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Interlocuteur unique dédié</li>
              <li>• Support téléphonique 6j/7</li>
              <li>• Conseils personnalisés</li>
              <li>• Formation aux outils</li>
            </ul>
          </div>
        </div>

        <div className="text-center">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Découvrez le potentiel d'économies de votre cabinet
            </h3>
            <p className="text-lg text-gray-700 mb-6 max-w-2xl mx-auto">
              Notre audit gratuit vous révèle les opportunités d'optimisation et d'économies 
              réalisables grâce à l'externalisation. Analyse complète en 48h.
            </p>
            <button 
              onClick={onOpenAuditModal}
              className="bg-blue-900 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Commencer l'audit gratuit
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}